import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useTheme } from '../../hooks/useTheme';
import { COLORS, FONTS } from '../../styles/tokens';
import { blueBtnStyle } from '../../styles/admin-styles';
import { fetchOrgTree, saveOrgTree } from '../../api/keycloak-admin';
import type { OrgTreeConfig, OrgTreeNode, OrgSector, StoredSectorTemplate } from '../../api/keycloak-admin';
import { OrgSetupHeader } from '../../components/OrgSetupHeader';
import { OrgTreeColumn } from '../../components/OrgTreeColumn';
import { UnitConfigPanel } from '../../components/UnitConfigPanel';
import { loadTemplates, findTemplate } from '../../services/template-service';
import {
  findNodeById, updateNodeById, removeNodeById,
  addChildToNode, deepCloneWithNewIds, createEmptyNode,
} from '../../utils/org-tree-helpers';

/** Organization management admin page with hierarchical tree editor */
export function OrganizationManagement(): React.JSX.Element {
  const { token } = useAuth();
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;

  const [orgTree, setOrgTree] = useState<OrgTreeConfig | null>(null);
  const [orgName, setOrgName] = useState('');
  const [sector, setSector] = useState<OrgSector | ''>('');
  const [branchOrType, setBranchOrType] = useState('');
  const [orgPlugins, setOrgPlugins] = useState<string[]>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [templates, setTemplates] = useState<StoredSectorTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [tree, templateList] = await Promise.all([
        fetchOrgTree(token).catch(() => null),
        loadTemplates(token).catch(() => []),
      ]);
      setTemplates(templateList);
      if (tree) {
        setOrgTree(tree);
        setOrgName(tree.name);
        setSector(tree.sector);
        setBranchOrType(tree.branchOrType);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async () => {
    if (!orgTree) return;
    try {
      setSaving(true);
      setError(null);
      const updated: OrgTreeConfig = {
        ...orgTree,
        name: orgName,
        sector: sector as OrgSector,
        branchOrType,
      };
      await saveOrgTree(token, updated);
      setOrgTree(updated);
      setDirty(false);
      setSuccess('Organization saved');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const applyTemplate = () => {
    if (!sector || !branchOrType) return;
    const template = findTemplate(templates, sector, branchOrType);
    if (!template) return;
    const units = deepCloneWithNewIds(template.tree);
    setOrgTree({
      id: crypto.randomUUID(),
      name: orgName || branchOrType,
      sector,
      branchOrType,
      units,
    });
    setOrgPlugins(template.suggestedPlugins);
    setDirty(true);
  };

  const handleAddChild = (parentId: string) => {
    if (!orgTree) return;
    const child = createEmptyNode('New Unit');
    setOrgTree({ ...orgTree, units: addChildToNode(orgTree.units, parentId, child) });
    setDirty(true);
  };

  const handleAddRoot = () => {
    if (!orgTree) {
      setOrgTree({
        id: crypto.randomUUID(),
        name: orgName,
        sector: sector as OrgSector,
        branchOrType,
        units: [createEmptyNode('Root Unit')],
      });
    } else {
      setOrgTree({ ...orgTree, units: [...orgTree.units, createEmptyNode('New Unit')] });
    }
    setDirty(true);
  };

  const handleDelete = (nodeId: string) => {
    if (!orgTree) return;
    setOrgTree({ ...orgTree, units: removeNodeById(orgTree.units, nodeId) });
    if (selectedNodeId === nodeId) setSelectedNodeId(null);
    setDirty(true);
  };

  const handleNodeUpdate = (updates: Partial<OrgTreeNode>) => {
    if (!orgTree || !selectedNodeId) return;
    setOrgTree({ ...orgTree, units: updateNodeById(orgTree.units, selectedNodeId, updates) });
    setDirty(true);
  };

  const selectedNode = orgTree && selectedNodeId
    ? findNodeById(orgTree.units, selectedNodeId)
    : null;

  const saveBtnStyle = blueBtnStyle(isDark, !dirty || saving);

  if (loading) {
    return <p style={{ fontFamily: FONTS.body, color: colors.textSecondary }}>Loading organizations...</p>;
  }

  if (error && !orgTree) {
    return (
      <div>
        <h2 style={{ fontFamily: FONTS.heading, fontSize: '18px', letterSpacing: '0.15em', color: colors.text, textTransform: 'uppercase', margin: '0 0 16px 0' }}>
          Organizations
        </h2>
        <div style={{ padding: '24px', borderRadius: '10px', border: `1px solid ${colors.border}`, textAlign: 'center' }}>
          <p style={{ fontFamily: FONTS.body, fontSize: '14px', color: '#EF4444', marginBottom: '12px' }}>{error}</p>
          <button onClick={loadData} style={{ padding: '8px 16px', borderRadius: '6px', border: `1px solid ${colors.border}`, backgroundColor: 'transparent', color: colors.text, fontFamily: FONTS.body, fontSize: '12px', cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="vx-page-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '12px' }}>
        <h2 className="vx-track" style={{ fontFamily: FONTS.heading, fontSize: '18px', letterSpacing: '0.15em', color: colors.text, textTransform: 'uppercase', margin: 0 }}>
          Organizations
        </h2>
        <button onClick={handleSave} disabled={!dirty || saving} style={saveBtnStyle}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {error && <StatusMsg text={error} type="error" isDark={isDark} />}
      {success && <StatusMsg text={success} type="success" isDark={isDark} />}

      <OrgSetupHeader
        orgName={orgName} sector={sector} branchOrType={branchOrType}
        isDark={isDark} colors={colors} templates={templates}
        onOrgNameChange={(n) => { setOrgName(n); setDirty(true); }}
        onSectorChange={(s) => { setSector(s); setBranchOrType(''); }}
        onTypeChange={setBranchOrType}
        onApplyTemplate={applyTemplate}
        hasExistingTree={Boolean(orgTree && orgTree.units.length > 0)}
      />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', minHeight: '400px' }}>
        <OrgTreeColumn
          units={orgTree?.units ?? []} selectedNodeId={selectedNodeId}
          isDark={isDark} colors={colors}
          onSelect={setSelectedNodeId} onAddChild={handleAddChild}
          onDelete={handleDelete} onAddRoot={handleAddRoot}
        />
        <UnitConfigPanel
          node={selectedNode} plugins={[]} orgPlugins={orgPlugins}
          isDark={isDark} colors={colors}
          onUpdate={handleNodeUpdate}
        />
      </div>
    </div>
  );
}

/** Status message */
function StatusMsg({ text, type, isDark }: { text: string; type: 'error' | 'success'; isDark: boolean }): React.JSX.Element {
  const isError = type === 'error';
  return (
    <div style={{
      padding: '12px 16px', borderRadius: '8px', marginBottom: '16px',
      backgroundColor: isDark ? (isError ? '#3b1c1c' : '#1c3b1c') : (isError ? '#fde8e8' : '#e8fde8'),
      border: `1px solid ${isError ? '#EF4444' : '#4ADE80'}`,
      color: isError ? '#EF4444' : '#4ADE80',
      fontFamily: FONTS.body, fontSize: '13px',
    }}>
      {text}
    </div>
  );
}
